import { Suspense } from 'react';
import PropTypes from 'prop-types';
import { Inline, Radio, vars } from '@etg/wings';
import { ExpandableContent, LoadingDots } from '@eti/components';
import { Bank, CreditCard } from '@eti/types';
import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import PaymentMethodLogo from '../../components/PaymentMethodLogo';
import { paymentMethodCodes, paymentMethodTypes } from '../../constants/paymentMethods';
import usePaymentMethodFees from '../../hooks/usePaymentMethodFees';
import { getPaymentMethodLogoId } from '../../utils/paymentMethods';
import { shouldShowBancontactCardLogo } from '../card/utils/bancontact';

const { PAY_INTERNAL } = paymentMethodTypes;
const {
  AFFIRM,
  ALIPAY_CN,
  ALIPAY_HK,
  APP_CARD,
  APPLE_PAY,
  BANCONTACT,
  CARD,
  DUMMY_BANK,
  GOOGLE_PAY,
  IDEAL,
  KLARNA_ONE,
  KLARNA_PAY_LATER,
  KLARNA_PAY_NOW,
  KLARNA_SLICE_IT,
  KLARNA_SOFORT,
  ONLINE_BANKING_PL,
  PAYPAL,
  PIX,
  SOFORT,
  TRUSTLY,
  UPI,
  VOLT,
  TWINT,
} = paymentMethodCodes;

const wrapperStyles = (hidePaymentMethod) => css`
  border: 1px solid ${vars.colors.divider};
  border-radius: ${vars.card.borderRadius};
  ${hidePaymentMethod && 'display: none;'}
`;

const loadingDotsStyles = css`
  display: flex;
  justify-content: center;
`;

const labelStyles = css`
  display: block;
  padding: 10px;
`;

const radioStyles = css`
  min-height: 36px;
`;

const feeStyles = (isDiscount) => css`
  color: ${isDiscount ? vars.colors.signal.positive.dark : '#666'};
  font-size: 0.75rem;
  font-weight: 400;
  text-transform: initial;
`;

const PaymentMethod = ({ code, name, paymentForm, selectedMainPaymentMethod, type, ...method }) => {
  const { t } = useTranslation();
  const { isDiscount, label } = usePaymentMethodFees({ ...method, type, code });
  const paymentMethodTitles = {
    [AFFIRM]: name,
    [ALIPAY_CN]: name,
    [ALIPAY_HK]: name,
    [APP_CARD]: t('Payment.Method.AppCard.Text'),
    [APPLE_PAY]: name,
    [BANCONTACT]: name,
    [CARD]: t('PaymentPage.Payment.Method.ButtonText.Card'),
    [GOOGLE_PAY]: name,
    [IDEAL]: name,
    [KLARNA_ONE]: name,
    [KLARNA_PAY_LATER]: t('Payment.Method.Klarna.PayLater.Text'),
    [KLARNA_PAY_NOW]: t('Payment.Method.Klarna.PayNow.Text'),
    [KLARNA_SLICE_IT]: t('Payment.Method.Klarna.SliceIt.Text'),
    [KLARNA_SOFORT]: t('Payment.Method.Klarna.Sofortuberweisung.Text'),
    [ONLINE_BANKING_PL]: name,
    [PAYPAL]: name,
    [PIX]: name,
    [SOFORT]: name,
    [TRUSTLY]: name,
    [DUMMY_BANK]: name,
    [UPI]: t('Payment.Method.Upi.Text'),
    [VOLT]: t('Payment.Method.Volt.Text'),
    [TWINT]: name,
  };
  const isPayInternalPayment = type === PAY_INTERNAL;
  const isSelected = code === selectedMainPaymentMethod;
  const PaymentMethodFormComponent = isSelected ? paymentForm : null;
  const shouldHidePaymentMethodLogos =
    selectedMainPaymentMethod === CARD && type === paymentMethodTypes.CARD;
  const creditCards = type === paymentMethodTypes.CARD ? method?.creditCards : [];

  return (
    <div
      className={wrapperStyles(isPayInternalPayment)}
      data-testid={`payment-method-${code}-wrapper`}
    >
      <Radio.Label className={labelStyles} data-testid={`paymentMethods-${code}-label`}>
        <Inline alignY="center" className={radioStyles} spacing={8}>
          <Radio data-testid="payment-method-input" id={code} value={code} />
          <div>
            <strong>{paymentMethodTitles[code]}</strong>
            <div
              className={feeStyles(isDiscount)}
              data-testid={`payment-paymentMethodFee-label-${code}`}
            >
              {label}
            </div>
          </div>
          {!isPayInternalPayment && !shouldHidePaymentMethodLogos && (
            <PaymentMethodLogo
              id={getPaymentMethodLogoId(code, shouldShowBancontactCardLogo(creditCards))}
            />
          )}
        </Inline>
      </Radio.Label>
      <ExpandableContent isExpanded={isSelected} variant="tall">
        <Suspense fallback={<LoadingDots containerStyle={loadingDotsStyles} />}>
          {PaymentMethodFormComponent}
        </Suspense>
      </ExpandableContent>
    </div>
  );
};

PaymentMethod.propTypes = {
  banks: PropTypes.arrayOf(Bank),
  code: PropTypes.string,
  creditCards: PropTypes.arrayOf(CreditCard),
  name: PropTypes.string,
  paymentForm: PropTypes.element,
  price: PropTypes.shape({
    value: PropTypes.number,
  }),
  selectedMainPaymentMethod: PropTypes.string,
  text: PropTypes.string,
  type: PropTypes.string,
};

export default PaymentMethod;
